import React, { useEffect, useState } from 'react';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import { DialogAction } from '../../../function/useModal';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@material-ui/core';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@material-ui/core';
import { GetSideBandParam, SideBandParam, GetSideBandCalc, SideBandCalc } from './FFTComparisonConanair';
import NumericEntry from '../../atoms/Input/NumericEntry';
import { useTranslation } from 'react-i18next';

type FFTSideBandDiagDialogProps = {
  dialog: DialogAction<FFTSideBandDiagDialogResponse>;
  plcNo: number;
  aiCard: number;
  aiCh: number;
  target: string;
};

export type FFTSideBandDiagDialogResponse = {};

const useStyles = makeStyles(
  createStyles({
    tableContainer: {
      maxHeight: 'calc(100vh - 300px)',
    },
    tableHeader: {
      borderWidth: '1px',
      borderColor: '#dcdcdc',
      borderStyle: 'solid',
      backgroundColor: 'grey',
      textAlign: 'center',
      color: 'white',
      padding: '1px',
      minWidth: '80px',
      borderCollapse: `collapse`,
      whiteSpace: 'pre-line',
    },
    tableCell: {
      borderWidth: '1px',
      borderColor: '#dcdcdc',
      orderBottomStyle: 'inset',
      borderLeftStyle: 'solid',
      borderRightStyle: 'solid',
      textAlign: 'center',
      minWidth: '80px',
      height: '8px',
      padding: '1px',
    },
    bandSet: {
      marginBottom: '4px',
    },
    entryBand: {
      width: '40px',
    },
    button: {
      width: '150px',
    },
  })
);

const FFTConanairSideBandDiagDialog: React.FC<FFTSideBandDiagDialogProps> = (props: FFTSideBandDiagDialogProps) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [mode, setMode] = useState<boolean>(true);
  const [freq, setFreq] = useState<string>('');
  const [slide, setSlide] = useState<string>('');
  const [bandWidth, setBandWidth] = useState<string>('');
  const [lowerSideBand, setLowerSideBand] = useState<string>('');
  const [upperSideBand, setUpperSideBand] = useState<string>('');
  const [dbFreq, setDbFreq] = useState<string>('');
  const [dbValue, setDbValue] = useState<string>('');
  const [freqMinus, setFreqMinus] = useState<string>('');
  const [dbMinus, setDbMinus] = useState<string>('');
  const [dbMinusDiff, setDbMinusDiff] = useState<string>('');
  const [freqPlus, setFreqPlus] = useState<string>('');
  const [dbPlus, setDbPlus] = useState<string>('');
  const [dbPlusDiff, setDbPlusDiff] = useState<string>('');
  const [entBand, setEntBand] = useState<number>(0);

  useEffect(() => {
    GetSideBandParam(props.plcNo, props.aiCard, props.aiCh, props.target).then((prm: SideBandParam) => {
      setFreq(prm.Freq);
      setSlide(prm.Slide);
      setBandWidth(prm.BandWidth);
      setLowerSideBand(prm.LowerSideBand);
      setUpperSideBand(prm.UpperSideBand);
      setDbFreq(prm.DBFreq);
      setDbValue(prm.DBValue);
    });
  }, [props.plcNo, props.aiCard, props.aiCh, props.target]);

  const calcSideband = (band: number) => {
    GetSideBandCalc(props.plcNo, props.aiCard, props.aiCh, props.target, entBand).then((prm: SideBandCalc) => {
      setFreqMinus(prm.FreqMinus);
      setDbMinus(prm.DBMinus);
      setDbMinusDiff(prm.DBDiffMinus);
      setFreqPlus(prm.FreqPlus);
      setDbPlus(prm.DBPlus);
      setDbPlusDiff(prm.DBDiffPlus);
    });
  };

  return (
    <>
      <Dialog
        open={isOpen}
        fullScreen={false}
        fullWidth={true}
        maxWidth="lg"
        onEntered={(e) => { }}
        onExited={(e) => {
          if (mode) {
            props.dialog.apply({});
          } else {
            props.dialog.cancel();
          }
        }}
      >
        <DialogTitle>{t('FFTConanairSideBandDiagDialog.側帯波診断', '側帯波診断')}</DialogTitle>
        <DialogContent>
          <div className={classes.bandSet}>
            <span>{t('FFTConanairSideBandDiagDialog.帯域設定 ±', '帯域設定 ±')}</span>
            <NumericEntry className={classes.entryBand} value={entBand} fixed={0} onChange={(val) => setEntBand(val)} />
            <span style={{ margin: '4px' }}>％</span>
            <Button
              variant="contained"
              color="primary"
              onClick={(e) => {
                calcSideband(entBand);
              }}
            >
              {t('FFTConanairSideBandDiagDialog.算出', '算出')}
            </Button>
          </div>
          <TableContainer className={classes.tableContainer}>
            <Table aria-label="sideBandDiag" size="small">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.tableHeader} colSpan={5}>
                    {t('FFTConanairSideBandDiagDialog.理論計算値', '理論計算値')}
                  </TableCell>
                  <TableCell className={classes.tableHeader} colSpan={2}>
                    ｆ
                  </TableCell>
                  <TableCell className={classes.tableHeader} colSpan={3}>
                    (1-2s)f
                  </TableCell>
                  <TableCell className={classes.tableHeader} colSpan={3}>
                    (1+2s)f
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className={classes.tableHeader}>{t('FFTConanairSideBandDiagDialog.f', 'f')}
                  </TableCell>
                  <TableCell className={classes.tableHeader}>{t('FFTConanairSideBandDiagDialog.すべり', 'すべり')}
                  </TableCell>
                  <TableCell className={classes.tableHeader}>{t('FFTConanairSideBandDiagDialog.2sf', '2sf')}
                  </TableCell>
                  <TableCell className={classes.tableHeader}>{t('FFTConanairSideBandDiagDialog.－側', '－側')}
                  </TableCell>
                  <TableCell className={classes.tableHeader}>{t('FFTConanairSideBandDiagDialog.＋側', '＋側')}
                  </TableCell>
                  <TableCell className={classes.tableHeader}>{t('FFTConanairSideBandDiagDialog.周波数', '周波数')}
                  </TableCell>
                  <TableCell className={classes.tableHeader}>{t('FFTConanairSideBandDiagDialog.ｄＢ値', 'ｄＢ値')}</TableCell>
                  <TableCell className={classes.tableHeader}>{t('FFTConanairSideBandDiagDialog.周波数', '周波数')}
                  </TableCell>
                  <TableCell className={classes.tableHeader}>{t('FFTSideBandDiagDialog.ｄＢ値', 'ｄＢ値')}</TableCell>
                  <TableCell className={classes.tableHeader}>
                    {t('FFTConanairSideBandDiagDialog.差分', '差分')}
                    <br />
                    {t('FFTConanairSideBandDiagDialog.ｄＢ値', 'ｄＢ値')}
                  </TableCell>
                  <TableCell className={classes.tableHeader}>{t('FFTConanairSideBandDiagDialog.周波数', '周波数')}
                  </TableCell>
                  <TableCell className={classes.tableHeader}>{t('FFTConanairSideBandDiagDialog.ｄＢ値', 'ｄＢ値')}</TableCell>
                  <TableCell className={classes.tableHeader}>
                    {t('FFTConanairSideBandDiagDialog.差分', '差分')}
                    <br />
                    {t('FFTConanairSideBandDiagDialog.ｄＢ値', 'ｄＢ値')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell className={classes.tableCell}>{freq}</TableCell>
                  <TableCell className={classes.tableCell}>{slide}</TableCell>
                  <TableCell className={classes.tableCell}>{bandWidth}</TableCell>
                  <TableCell className={classes.tableCell}>{lowerSideBand}</TableCell>
                  <TableCell className={classes.tableCell}>{upperSideBand}</TableCell>
                  <TableCell className={classes.tableCell}>{dbFreq}</TableCell>
                  <TableCell className={classes.tableCell}>{dbValue}</TableCell>
                  <TableCell className={classes.tableCell}>{freqMinus}</TableCell>
                  <TableCell className={classes.tableCell}>{dbMinus}</TableCell>
                  <TableCell className={classes.tableCell}>{dbMinusDiff}</TableCell>
                  <TableCell className={classes.tableCell}>{freqPlus}</TableCell>
                  <TableCell className={classes.tableCell}>{dbPlus}</TableCell>
                  <TableCell className={classes.tableCell}>{dbPlusDiff}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button
            className={classes.button}
            style={{ marginRight: '30px' }}
            color="primary"
            variant="contained"
            onClick={(e) => {
              setIsOpen(false);
              setMode(false);
            }}
          >
            {t('FFTConanairSideBandDiagDialog.閉じる', '閉じる')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FFTConanairSideBandDiagDialog;
